<script setup>
import { ref, computed, nextTick } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import emojisGroup from 'shared/components/emoji/emojisGroup.json';

const props = defineProps({
  reactions: {
    type: Array,
    default: () => [],
  },
  currentUserId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['select', 'remove', 'close']);

const QUICK_EMOJIS = [
  { emoji: '\uD83D\uDC4D', label: 'thumbs up' },
  { emoji: '\u2764\uFE0F', label: 'heart' },
  { emoji: '\uD83D\uDE02', label: 'joy' },
  { emoji: '\uD83D\uDE2E', label: 'surprised' },
  { emoji: '\uD83D\uDE22', label: 'sad' },
  { emoji: '\uD83D\uDE4F', label: 'pray' },
  { emoji: '\uD83D\uDD25', label: 'fire' },
  { emoji: '\uD83C\uDF89', label: 'party' },
];

const isOpen = ref(false);
const openUpward = ref(true);
const containerRef = ref(null);
const searchQuery = ref('');

const filteredEmojis = computed(() => {
  if (!searchQuery.value.trim()) return null;
  const q = searchQuery.value.trim().toLowerCase();
  const results = [];
  for (const category of emojisGroup) {
    for (const emoji of category.emojis) {
      if (emoji.slug.replaceAll('_', ' ').includes(q) || emoji.name.toLowerCase().includes(q)) {
        results.push(emoji);
      }
    }
  }
  return results.slice(0, 48);
});

async function toggle() {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchQuery.value = '';
    await nextTick();
    if (containerRef.value) {
      const rect = containerRef.value.getBoundingClientRect();
      // picker height ~290px; if less space above, open downward
      openUpward.value = rect.top > 300;
    }
  }
}

function selectEmoji(emoji) {
  const existingReaction = props.reactions.find(
    r => r.emoji === emoji && r.user_id === props.currentUserId
  );
  if (existingReaction) {
    emit('remove', existingReaction.id);
  } else {
    emit('select', emoji);
  }
  isOpen.value = false;
}

function close() {
  isOpen.value = false;
  emit('close');
}
</script>

<template>
  <div ref="containerRef" class="relative">
    <button
      class="flex items-center justify-center rounded p-1 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
      @click="toggle"
    >
      <Icon icon="i-lucide-smile-plus" class="size-4" />
    </button>
    <div
      v-if="isOpen"
      v-on-click-outside="close"
      class="absolute right-0 z-50 w-64 rounded-lg border border-n-slate-6 bg-n-solid-2 shadow-lg"
      :class="openUpward ? 'bottom-full mb-1' : 'top-full mt-1'"
    >
      <!-- Quick reactions row -->
      <div class="grid grid-cols-8 gap-0.5 border-b border-n-slate-5 p-1.5">
        <button
          v-for="item in QUICK_EMOJIS"
          :key="item.label"
          class="flex items-center justify-center rounded p-1 text-base hover:bg-n-alpha-2"
          :title="item.label"
          @click="selectEmoji(item.emoji)"
        >
          {{ item.emoji }}
        </button>
      </div>
      <!-- Search -->
      <div class="px-2 py-1.5">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search emojis..."
          class="w-full rounded border border-n-slate-6 bg-n-solid-1 px-2 py-1 text-xs text-n-slate-12 placeholder-n-slate-10 outline-none focus:border-n-brand"
        />
      </div>
      <!-- Emoji grid -->
      <div class="h-44 overflow-y-auto p-1.5">
        <!-- Search results -->
        <template v-if="filteredEmojis !== null">
          <div
            v-if="filteredEmojis.length === 0"
            class="flex h-full items-center justify-center text-xs text-n-slate-10"
          >
            No emojis found
          </div>
          <div v-else class="grid grid-cols-8 gap-0.5">
            <button
              v-for="item in filteredEmojis"
              :key="item.slug"
              class="flex items-center justify-center rounded p-1 text-base hover:bg-n-alpha-2"
              :title="item.name"
              @click="selectEmoji(item.emoji)"
            >
              {{ item.emoji }}
            </button>
          </div>
        </template>
        <!-- All emojis by category -->
        <template v-else>
          <div v-for="category in emojisGroup" :key="category.slug" class="mb-2">
            <p class="mb-0.5 px-1 text-xs font-medium capitalize text-n-slate-9">
              {{ category.name }}
            </p>
            <div class="grid grid-cols-8 gap-0.5">
              <button
                v-for="item in category.emojis"
                :key="item.slug"
                class="flex items-center justify-center rounded p-1 text-base hover:bg-n-alpha-2"
                :title="item.name"
                @click="selectEmoji(item.emoji)"
              >
                {{ item.emoji }}
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
